import { hasPermission } from 'core/auth/rbac'
import { GrievancesIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { GrievanceRecordWithBuyer } from '../hooks/useGrievance'

interface GrievanceListProps {
  records: GrievanceRecordWithBuyer[]
  loading: boolean
  onAssign: (record: GrievanceRecordWithBuyer) => void
  onResolve: (record: GrievanceRecordWithBuyer) => void
  onView: (record: GrievanceRecordWithBuyer) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
}

const formatDate = (val: string) => {
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const grievanceStatusTones: Record<string, 'success' | 'warning' | 'danger'> = {
  resolved: 'success',
  in_progress: 'warning',
  open: 'danger',
}

const getGrievanceStatusTone = (status: string): 'success' | 'warning' | 'danger' => {
  return grievanceStatusTones[status] || 'danger'
}

export function GrievanceList({
  records,
  loading,
  onAssign,
  onResolve,
  onView,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}: GrievanceListProps) {
  const { user } = useAuth()
  const canDelegate = hasPermission(user, 'delegate_grievances')
  const canResolve = hasPermission(user, 'resolve_grievances')

  const columns: Column<GrievanceRecordWithBuyer>[] = [
    {
      key: 'id',
      label: 'Grievance ID',
      className: '!align-top font-semibold text-text-primary',
    },
    {
      key: 'orderId',
      label: 'Linked Order ID',
      className: '!align-top font-mono text-text-secondary text-sm',
    },
    {
      key: 'buyer',
      label: 'FPO / Retailer',
      className: '!align-top !whitespace-normal min-w-[150px]',
      render: (row) => (
        <div className="flex flex-col gap-1.5">
          <span className="text-text-primary text-sm font-semibold break-words">{row.buyerName || '—'}</span>
          {row.buyerType && (
            <Badge tone="secondary" variant="outline" className="capitalize w-fit text-[10px] px-1 py-0">
              {row.buyerType}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'discrepancy',
      label: 'Quantity vs Issue',
      className: '!align-top !whitespace-normal min-w-[280px]',
      render: (row) => (
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-text-primary">
            {row.orderedUnits > 0 && (
              <>
                <span>Ord: {row.orderedUnits}</span>
                <span className="text-text-secondary font-normal text-[10px]">•</span>
                <span>Recd: {row.receivedUnits}</span>
              </>
            )}
            <Badge tone="danger" variant="subtle" className="text-[10px] px-1 py-0 font-bold shrink-0 ml-1">
              {row.affectedUnits || 0} Units
            </Badge>
          </div>
          <span
            className="text-[11px] text-text-secondary font-medium leading-tight max-w-[320px] break-words line-clamp-3"
            title={row.issue}
          >
            {row.issue}
          </span>
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Logged Date',
      className: '!align-top',
      render: (row) => <span>{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'assignedAmName',
      label: 'Assigned AM',
      className: '!align-top',
      render: (row) => (
        <span className="text-text-primary text-sm font-semibold">{row.assignedAmName || 'Unassigned'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      className: '!align-top',
      render: (row) => {
        const tone = getGrievanceStatusTone(row.status)
        return (
          <Badge tone={tone} variant="subtle" className="capitalize">
            {row.status.replace('_', ' ')}
          </Badge>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      className: '!align-top text-center',
      render: (row) => {
        const isResolved = row.status === 'resolved'
        const isAssigned = !!row.assignedAmId

        return (
          <div className="flex items-center justify-center gap-2 min-w-[120px]">
            <button
              onClick={() => onView(row)}
              className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              View
            </button>
            {!isResolved && !isAssigned && canDelegate && (
              <button
                onClick={() => onAssign(row)}
                className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-btn hover:bg-primary-hover transition-colors w-[84px] text-center"
              >
                Assign To
              </button>
            )}
            {!isResolved && isAssigned && canResolve && (
              <button
                onClick={() => onResolve(row)}
                className="px-2 py-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-btn hover:bg-secondary-hover transition-colors w-[84px] text-center"
              >
                Resolve
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <Table
      columns={columns}
      data={records}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No discrepancies raised in this queue."
      emptyIcon={<GrievancesIcon size={20} className="opacity-60" />}
    />
  )
}
